/**
 * AgencySwitcher — topbar dropdown for multi-agency users.
 *
 * Single-agency users see a plain label (no dropdown). Multi-agency users
 * see a clickable button that opens a list of their agencies and switches
 * the active tenant via POST /api/auth/switch-agency, then reloads the page
 * so all agency-scoped data refreshes.
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Building2, ChevronDown, Check, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AgencySwitcher({ fallbackName = "Afyanex Care" }) {
  const { user, switchAgency } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  const agencies = user?.agencies || [];
  const activeId = user?.active_agency_id;
  const activeName = agencies.find((a) => a.id === activeId)?.name || fallbackName;

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handlePick = async (agencyId) => {
    if (agencyId === activeId) { setOpen(false); return; }
    setSwitching(true);
    try {
      await switchAgency(agencyId);
      // Hard reload so every agency-scoped query (clients, shifts, settings…)
      // re-fetches against the new tenant cleanly. Cheaper than wiring a
      // global "active_agency_id changed" event into every component.
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch agency.");
      setSwitching(false);
      setOpen(false);
    }
  };

  // Single-agency (or pre-multi-agency cached profile): render the label as-is.
  if (agencies.length <= 1) {
    return (
      <div data-testid="topbar-agency-name">{activeName}</div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        className="group inline-flex items-center gap-1.5 -ml-1 px-1.5 py-0.5 rounded-md hover:bg-clinical-bg transition-colors disabled:opacity-60"
        data-testid="topbar-agency-switcher-button"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-clinical-text truncate max-w-[180px]">
          {activeName}
        </span>
        {switching ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-clinical-subtext" />
        ) : (
          <ChevronDown className={`h-3.5 w-3.5 text-clinical-subtext transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="menu"
          className="absolute left-0 top-full mt-1.5 w-72 rounded-xl border border-clinical-border bg-white shadow-[0_12px_32px_rgba(11,21,48,0.10)] z-30 overflow-hidden"
          data-testid="topbar-agency-switcher-panel"
        >
          <div className="px-3.5 py-2.5 border-b border-clinical-border bg-clinical-bg/60">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-clinical-subtext">
              Switch agency
            </p>
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {agencies.map((a) => {
              const isActive = a.id === activeId;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handlePick(a.id)}
                  disabled={switching}
                  className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-clinical-bg transition-colors disabled:opacity-60 ${isActive ? "bg-clinical-primary/[0.04]" : ""}`}
                  data-testid={`topbar-agency-switcher-item-${a.slug || a.id}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="shrink-0 h-7 w-7 rounded-md bg-clinical-primary/10 text-clinical-primary flex items-center justify-center">
                      <Building2 className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-clinical-text truncate">{a.name}</div>
                      <div className="text-[11px] text-clinical-subtext truncate">{a.slug}</div>
                    </div>
                  </div>
                  {isActive && (
                    <Check className="h-4 w-4 text-clinical-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
          <a
            href="/agency/signup"
            className="flex items-center gap-2 px-3.5 py-2.5 border-t border-clinical-border text-xs font-medium text-clinical-primary hover:bg-clinical-bg transition-colors"
            data-testid="topbar-agency-switcher-add-link"
          >
            <Plus className="h-3.5 w-3.5" />
            Add another agency
          </a>
        </div>
      )}
    </div>
  );
}
